import React from "react";
import {
  Image,
  Platform,
  Pressable,
  SectionList,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import { FAQConstants, getCompanyName, NavigationScreenName, SIGN_OUT_REGEX } from "@lib/utilities/Constants";
import { theme } from "../../theme/theme";
import { COLORS } from "../../theme/constants";
import StyleSheet from "react-native-media-query";
import {
  BACK_ICON_WHITE,
  CROSS_ICON_WHITE,
} from "../../resources/IMAGES";
import { clearSessionAndLogout } from "@lib/utilities/AuthHelper";
import { useNavigation } from "@react-navigation/native";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@lib";
import { CustomDrawerProps } from "./CustomDrawerTypes";
import { useDrawerActions } from "@lib/hooks/drawer/useDrawerActions";
import { drawerData, FaqJsonData } from "src/resources/strings";
import FaqSection from "../faq-section/FaqSection";

const CustomDrawer: React.FC<CustomDrawerProps> = ({
  moveToResetPasswordScreen,
  moveToDeleteAccountScreen,
  closeRightPanel,
  userCredential,
  userName,
  email,
}) => {
  const navigation = useNavigation();
  const { logout } = useAuth0();
  const isConnected = useAppSelector(
    (state) => state?.userReducer?.isConnected,
  );
  const { height } = useWindowDimensions();
  const user = useAppSelector((state) => state.userReducer);
  const firstName = user?.profile?.first_name;
  const companyName = getCompanyName()?.toUpperCase();

  const { handleItemPress, faqActive, setFaqActive } =
    useDrawerActions({
      navigation,
      logout,
      isConnected,
      closeRightPanel,
      moveToResetPasswordScreen,
      moveToDeleteAccountScreen,
      clearSessionAndLogout,
    });

  const CloseBtn = () => (
    <TouchableOpacity
      onPress={() => {
        closeRightPanel();
      }}
      style={styles.crossIconTouch}
    >
      <Image
        style={styles.crossIcon}
        dataSet={{ media: ids.crossIcon }}
        source={CROSS_ICON_WHITE}
      />
    </TouchableOpacity>
  );
  const UserInitialsSection = () => (
    <View>
      <View style={styles.topSection}>
        <View style={styles.profileDetailContainer}>
          <View style={styles.initialsContainer}>
            <Text style={styles.userInitials}>{userCredential}</Text>
          </View>
        </View>
      </View>
      <View style={styles.userNameContainer}>
        <Text style={styles.userNameText} dataSet={{ media: ids.userNameText }}>
          {userName}
        </Text>
        {email && (
          <Text
            style={styles.userEmailText}
            dataSet={{ media: ids.userEmailText }}
          >
            {email}
          </Text>
        )}
      </View>
    </View>
  );
  const BackBtn = () => (
    <TouchableOpacity
      onPress={() => {
        setFaqActive(false)
      }}
      testID="side-panel-back-button"
      style={styles.backButton}
    >
      <Image
        style={styles.backButton}
        dataSet={{ media: ids.backButton }}
        source={BACK_ICON_WHITE}
        testID="side-panel-back-icon"
      />
    </TouchableOpacity>
  );

  return (
    <View
      style={styles.containerFluid}
      dataSet={{ media: ids.containerFluid }}
    >
      {faqActive ? (
        <>
          <View style={styles.faqTopSection}>
            <View style={styles.row}>
              <BackBtn />
              <Text style={styles.drawerTitle}>FAQs</Text>
            </View>
            <View style={styles.subTitleContianer}>
              <Text
                style={styles.faqSubtitle}
                dataSet={{ media: ids.faqSubtitle }}
              >
                Hi {firstName}! {FAQConstants.howCanWeHelp}
              </Text>
            </View>
          </View>

          <FaqSection
            data={FaqJsonData[companyName]}
            onNavigate={(href) => {
              if (href === "Reset Password") {
                closeRightPanel();
                navigation.navigate(NavigationScreenName.resetPassword);
              } else if (href === "Docs & Info") {
                closeRightPanel();
                navigation.navigate(NavigationScreenName.documentInfo);
              }
            }}
          />
        </>
      ) : (
        <>
          <CloseBtn />
          <UserInitialsSection />
          <View style={styles.drawerListContainer}>
            <SectionList
              style={styles.sectionList}
              sections={drawerData}
              keyExtractor={(item) =>
                item.id?.toString() || Math.random().toString()
              }
              showsVerticalScrollIndicator={false}
              scrollEnabled={true}
              stickySectionHeadersEnabled={false} // optional - change if you want sticky titles
              renderSectionHeader={({ section }) =>
                section.title ? (
                  <Text style={styles.listItemTitleLabel}>{section.title}</Text>
                ) : null
              }
              renderItem={({ item, index, section }) => {
                const currentIndex = index;
                const firstItem = currentIndex === 0;
                const lastItem = currentIndex === section.data.length - 1;
                const isSignout = item.id === "signout";

                return (
                  <View
                    style={[
                      styles.commonItemStyle,
                      !firstItem && !lastItem && styles.midItem,
                      firstItem && styles.firstItem,
                      lastItem && styles.lastItem,
                      item.label === "" && styles.singleItem,
                      SIGN_OUT_REGEX.test(item.label) && styles.signOutBtn,
                    ]}
                  >
                    <Pressable
                      style={styles.listItemRow}
                      onPress={() => handleItemPress(item)}
                    >
                      <Image source={item.icon} style={styles.listItemIcon} />
                      <Text
                        style={[
                          styles.listItemLabel,
                          isSignout && styles.signOutTxt,
                        ]}
                      >
                        {item.label}
                      </Text>
                    </Pressable>
                    {!lastItem && <View style={styles.divider} />}
                  </View>
                );
              }}
            />
          </View>
        </>
      )}
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  containerFluid: {
    flex: 1,
    display: "flex",
    backgroundColor: COLORS.offWhite,
  },
  faqDropdownCollapsedIcon: {
    width: 14,
    aspectRatio: 1,
    resizeMode: "contain",
  },
  faqDropdownExpandedIcon: {
    width: 14,
    resizeMode: "contain",
    transform: "rotate(180deg)",
  },
  faqQuestionContainer: {
    marginTop: theme.margin.md,
    borderWidth: 1,
    borderColor: COLORS.paleGray,
    borderRadius: 7,
    padding: theme.spacing.md,
  },
  faqQuestionTxt: {
    fontFamily: theme.fontFamily.robotoMedium,
    fontWeight: theme.fontweight.regular,
    fontSize: theme.fontsize.cs,
    color: COLORS.gray[600],
  },
  faqAnswerTxt: {
    fontFamily: theme.fontFamily.robotoRegular,
    fontSize: theme.fontsize.cs,
    color: COLORS.gray[600],
    lineHeight: 28,
    marginTop: theme.spacing.reg,
  },
  faqContentContainer: {
    marginHorizontal: theme.spacing.md,
  },
  subTitleContianer: {
    height: 100,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xlg,
  },
  faqSubtitle: {
    fontFamily: theme.fontFamily.robotoBold,
    fontWeight: theme.fontweight.bold,
    fontSize: theme.fontsize.cs,
    margin: "auto",
    color: theme.colors.white,
  },
  rowApart: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  faqTopSection: {
    backgroundColor: theme.colors.primary,
    padding: theme.spacing.md,
  },
  drawerTitle: {
    fontFamily: theme.fontFamily.robotoSlabBold,
    fontSize: theme.fontsize.mmd,
    color: theme.colors.white,
  },
  backButton: {
    padding:theme.spacing.ssm,
    paddingHorizontal:theme.spacing.sm,
    resizeMode: "contain",
  },
  sectionList: { paddingBottom: theme.margin.lg },
  commonItemStyle: {
    paddingHorizontal: 12,
    backgroundColor: theme.colors.white,
  },
  userNameContainer: {
    marginTop: theme.margin.xl,
    marginBottom: theme.margin.reg,
    padding: theme.spacing.reg,
  },
  listItemIcon: {
    width: theme.spacing.md,
    height: theme.spacing.md,
    resizeMode: "contain",
  },
  signOutTxt: { color: theme.colors.text.error },
  listItemTitleLabel: {
    fontSize: theme.fontsize.cs,
    color: COLORS.gray[600],
    fontFamily: theme.fontFamily.robotoMedium,
    marginBottom: theme.margin.ssm,
    marginTop: theme.margin.lg,
  },
  userEmailText: {
    fontSize: theme.fontsize.cs,
    color: COLORS.gray[500],
    fontFamily: theme.fontFamily.robotoRegular,
    marginTop: theme.margin.xs,
    textAlign: "center",
  },
  singleItem: {
    borderWidth: 1,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderRadius: 7,
    borderColor: COLORS.lightGray,
    backgroundColor: theme.colors.white,
    marginTop: 12,
  },
  signOutBtn: {
    borderWidth: 1,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    marginTop: theme.margin.lg,
  },
  divider: {
    width: "100%",
    height: 1,
    backgroundColor: COLORS.gray[300] + 40,
    marginVertical: theme.margin.reg,
  },
  profileDetailContainer: {
    marginTop: 60,
  },
  drawerListContainer: {
    flex:1,
    marginHorizontal: theme.spacing.md,
  },
  header: {
    fontFamily: theme.fontFamily.robotoBold,
    fontSize: theme.fontsize.reg,
    color: COLORS.gray[500],
    marginBottom: 12,
  },
  listItemLabel: {
    fontSize: theme.fontsize.cs,
    color: COLORS.gray[600],
    fontFamily: theme.fontFamily.robotoMedium,
  },
  listItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
  },
  firstItem: {
    borderColor: COLORS.lightGray,
    borderWidth: 1,
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7,
    borderBottomWidth: 0,
    paddingTop: theme.spacing.cs,
  },
  midItem: {
    borderWidth: 1,
    borderColor: COLORS.lightGray,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderTopWidth: 0,
  },
  lastItem: {
    borderColor: COLORS.lightGray,
    borderWidth: 1,
    borderBottomLeftRadius: 7,
    borderBottomRightRadius: 7,
    borderTopWidth: 0,
    paddingBottom: theme.spacing.cs,
  },
  initialsContainer: {
    backgroundColor: theme.colors.primary,
    borderRadius: 200,
    borderWidth: 6,
    borderColor: COLORS.white,
    width: 100,
    alignItems: "center",
    justifyContent: "center",
    margin: "auto",
    aspectRatio: 1,
  },
  userInitials: {
    color: COLORS.white,
    fontSize: theme.fontsize.xxl,
    textAlign: "center",
    fontFamily: theme.fontFamily.robotoSlabRegular,
  },
  userNameText: {
    color: theme.colors.black,
    fontSize: theme.fontsize.mmd,
    textAlign: "center",
    marginTop: theme.spacing.md,
    fontFamily: theme.fontFamily.robotoSlabBold,
    textTransform: "capitalize",
    ...Platform.select({
      ios: {},
      android: {},
      web: {
        fontWeight: theme.fontweight.medium as any,
      },
    }),
  },
  topSection: {
    backgroundColor: theme.colors.primary,
    height: 113,
  },
  crossIconTouch: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    zIndex: 99,
    top: 10,
    right: 10,
  },
  crossIcon: {
    width: 20,
    height: 20,
    "@media (min-width: 700px)": {
      // iPad Pro 9.7
      width: 18,
      height: 18,
    },
  },
});

export default CustomDrawer;
